import { MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';

/**
 * Resolves locale keys whose casing (or hyphen/spacing) does not exactly match
 * what the API returns. e.g. API "Savings period" -> locale "Savings Period",
 * API "Key stake-holders" -> locale "Key Stakeholders".
 *
 * Comparison is done on a normalized form: lowercased, with whitespace,
 * hyphens, underscores, and "&" removed. The first key that matches the
 * normalized form is used; falls back to the original key when no match.
 *
 * This lets us keep one canonical Title-Case entry per concept across all
 * locale files instead of duplicating it per casing the API might emit.
 */
export class TitleCaseMissingTranslationHandler implements MissingTranslationHandler {
  private readonly normalizedIndex = new Map<string, Map<string, string>>();
  private readonly indexedKeyCount = new Map<string, number>();

  handle(params: MissingTranslationHandlerParams): string {
    const key = params.key;
    if (typeof key !== 'string' || !key) return key;

    const svc = params.translateService;
    const lang = svc.currentLang || svc.defaultLang;
    if (!lang) return key;

    const translations = svc.translations?.[lang] as Record<string, unknown> | undefined;
    if (!translations) return key;

    const index = this.getOrBuildIndex(lang, translations);
    const canonical = index.get(this.normalize(key));
    if (canonical && canonical !== key) {
      const value = translations[canonical];
      if (typeof value === 'string') return value;
    }
    return key;
  }

  private getOrBuildIndex(lang: string, translations: Record<string, unknown>): Map<string, string> {
    const keys = Object.keys(translations);
    const cached = this.normalizedIndex.get(lang);
    if (cached && this.indexedKeyCount.get(lang) === keys.length) return cached;

    const index = new Map<string, string>();
    for (const k of keys) {
      const norm = this.normalize(k);
      if (!index.has(norm)) index.set(norm, k);
    }
    this.normalizedIndex.set(lang, index);
    this.indexedKeyCount.set(lang, keys.length);
    return index;
  }

  private normalize(s: string): string {
    return s.toLowerCase().replace(/[\s\-_&]+/g, '');
  }
}
